import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate, useParams } from 'react-router-dom'
import { HostingsAPI } from '../api'
import { HelpCircle, Image as ImageIcon, Users, Server } from 'lucide-react'
import { Toggle } from './Toggle'
import { IconToggle } from './IconToggle'
import { SecuritySlider } from './SecuritySlider'
import { RichEditor } from './RichEditor'
import { MediaManager } from './MediaManager'
import { useAdmin } from '../lib/hooks'
import { PaypalReturnUrl } from './PaypalReturnUrl'

const Field = ({ label, required, tooltip, error, children }: { label: string; required?: boolean; tooltip?: string; error?: string; children: React.ReactNode }) => (
  <div className="space-y-1">
    <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
      <span>{label}</span>
      {required ? <span className="text-red-600">*</span> : null}
      {tooltip ? <span title={tooltip}><HelpCircle className="w-4 h-4 text-gray-400" /></span> : null}
    </div>
    {children}
    {error ? <div className="text-xs text-red-600">{error}</div> : null}
  </div>
)

const AdminEditHosting = () => {
  const { isAdmin, loading: adminLoading } = useAdmin()
  const navigate = useNavigate()
  const { id } = useParams()
  const { getToken } = useAuth()

  const [form, setForm] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showMediaManager, setShowMediaManager] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const photosInputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const [isPinned, setIsPinned] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (!sentinelRef.current) return
      // The header is 4rem (64px) tall (top-16).
      // We want to pin when the sentinel (which is right above the bar) reaches that point.
      // So if window.scrollY is greater than the sentinel's initial position minus 64px.
      const stickPoint = sentinelRef.current.offsetTop - 64
      setIsPinned(window.scrollY > stickPoint)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    // Check initially in case we reload scrolled down
    handleScroll()
    
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty && !saving) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty, saving])

  useEffect(() => {
    const load = async () => {
      if (!id) return
      setLoading(true)
      const res = await HostingsAPI.get(id)
      setForm(res.data)
      setLoading(false)
    }
    if (isAdmin) load()
  }, [id, isAdmin])

  if (adminLoading) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Cargando permisos...</p></div>
  if (!isAdmin) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Acceso restringido</p></div>
  if (loading || !form) return <div className="max-w-7xl mx-auto p-6 mt-24"><p className="text-gray-600">Cargando...</p></div>

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as any
    setForm((prev: any) => ({ ...prev, [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value }))
    setDirty(true)
  }

  const onPickPhotos = () => {
    photosInputRef.current?.click()
  }
  const onUploadPhotos = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    
    setSaving(true)
    setMessage('Subiendo fotos...')
    const token = await getToken()
    const slugBase = (form.displayName || form.name || 'hosting').toString()
    const slug = slugBase.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    
    const newPhotos: string[] = []
    
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      try {
        const res = await fetch(`/api/upload-featured?slug=${encodeURIComponent(slug)}&suffix=gallery-${Date.now()}-${i}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token || ''}`, 'Content-Type': f.type },
          body: f,
        })
        const data = await res.json()
        if (res.ok && data?.url) {
          newPhotos.push(data.url)
        }
      } catch (err) {
        console.error(err)
      }
    }
    
    setForm((prev: any) => ({ ...prev, photos: [...(prev.photos || []), ...newPhotos] }))
    setSaving(false)
    setMessage(`Se subieron ${newPhotos.length} fotos`)
    setDirty(true)
  }

  const removePhoto = (idx: number) => {
    setForm((prev: any) => ({ ...prev, photos: (prev.photos || []).filter((_: any, i: number) => i !== idx) }))
    setDirty(true)
  }

  const errorClass = (name: string) => (errors[name] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '')

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name) e.name = 'Requerido'
    if (!form.displayName) e.displayName = 'Requerido'
    if (!form.currency) e.currency = 'Requerido'
    if (!form.price || Number(form.price) <= 0) e.price = 'Debe ser mayor a 0'
    if (!form.payType) e.payType = 'Requerido'
    if (!form.resources) e.resources = 'Requerido'
    if (!form.sites) e.sites = 'Requerido'
    if (!form.hostingSystem) e.hostingSystem = 'Requerido'
    if (!form.security) e.security = 'Requerido'
    if (!form.cache) e.cache = 'Requerido'
    if (!form.imageOptimization) e.imageOptimization = 'Requerido'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const onPickFeatured = () => {
    fileInputRef.current?.click()
  }
  const onUploadFeatured = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    const token = await getToken()
    const slugBase = (form.displayName || form.name || 'hosting').toString()
    const slug = slugBase.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    const res = await fetch(`/api/upload-featured?slug=${encodeURIComponent(slug)}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token || ''}`, 'Content-Type': f.type },
      body: f,
    })
    const data = await res.json()
    if (res.ok && data?.url) {
      setForm((prev: any) => ({ ...prev, mainPhotoUrl: data.url }))
      setMessage('Imagen destacada subida')
    } else {
      setMessage(data?.error || 'Error al subir imagen')
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      const ok = validate()
      if (!ok) {
        setSaving(false)
        return
      }
      await HostingsAPI.update(id as string, form)
      setMessage('Hosting actualizado')
      setDirty(false)
      navigate('/admin')
    } catch (err: any) {
      setMessage(err?.message || 'Error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-8 relative">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-3xl font-bold">Editar Hosting</h2>
          <button
            onClick={() => {
              if (!dirty || confirm('Hay cambios sin guardar. ¿Salir igualmente?')) navigate('/admin')
            }}
            className="px-3 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
          >
            Volver
          </button>
        </div>
        <div className="text-sm text-gray-500">
          <span className="cursor-pointer text-blue-600 hover:underline" onClick={() => navigate('/admin')}>Administración</span>
          <span> / Editar</span>
        </div>
      </div>

      <div ref={sentinelRef} className="h-px w-full pointer-events-none opacity-0" />
      <div className={`sticky top-16 z-20 transition-all duration-200 ${isPinned ? 'bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-md' : 'bg-gray-50 dark:bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`inline-block h-3 w-3 rounded-full shadow-sm transition-colors duration-300 ${dirty ? 'bg-amber-500' : 'bg-green-500'}`} />
              <span className="text-base font-medium text-gray-700">{dirty ? 'Cambios sin guardar' : 'Sin cambios'}</span>
              {message && <span className="text-sm text-blue-600 animate-pulse ml-2">{message}</span>}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!dirty || confirm('Hay cambios sin guardar. ¿Salir igualmente?')) navigate('/admin')
                }}
                className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => formRef.current?.requestSubmit()}
                className="px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-sm transition-all active:scale-95"
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-12">
        <form onSubmit={onSubmit} className="space-y-8" ref={formRef}>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Datos</h3>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-12 gap-6">
              {/* Left Column: Image */}
              <div className="col-span-12 md:col-span-4 lg:col-span-3">
                <Field label="Imagen destacada" tooltip="Imagen destacada del plan">
                  <div className="space-y-3">
                    <div className="w-full aspect-square bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden relative">
                      {form.mainPhotoUrl ? (
                        <img src={form.mainPhotoUrl} alt="Preview" className="w-full h-full object-contain p-4" />
                      ) : (
                        <ImageIcon className="w-16 h-16 text-gray-300" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input className="border rounded-md px-3 py-2 w-full text-sm" name="mainPhotoUrl" value={form.mainPhotoUrl || ''} onChange={onChange} placeholder="https://..." />
                      <button type="button" onClick={onPickFeatured} className="px-3 py-2 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium text-sm whitespace-nowrap">Subir</button>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onUploadFeatured} />
                    <p className="text-xs text-gray-500">Recomendado: PNG con transparencia, 130x130px</p>
                  </div>
                </Field>
              </div>

              {/* Right Column: Fields */}
              <div className="col-span-12 md:col-span-8 lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Orden" required tooltip="Orden de aparición (menor primero)" error={errors.sortOrder}>
                  <input className={`border rounded-md px-3 py-2 w-full ${errorClass('sortOrder')}`} type="number" name="sortOrder" value={form.sortOrder || 0} onChange={onChange} />
                </Field>
                <Field label="Visibilidad" required tooltip="Estado público del plan">
                  <select 
                    className="border rounded-md px-3 py-2 w-full" 
                    name="isVisible" 
                    value={form.isVisible !== false ? 'true' : 'false'} 
                    onChange={(e) => {
                      setForm((prev: any) => ({ ...prev, isVisible: e.target.value === 'true' }))
                      setDirty(true)
                    }}
                  >
                    <option value="true">Visible</option>
                    <option value="false">Oculto</option>
                  </select>
                </Field>

                <Field label="Nombre interno" required tooltip="Identificador sin espacios" error={errors.name}>
                  <input className={`border rounded-md px-3 py-2 w-full ${errorClass('name')}`} name="name" value={form.name} onChange={onChange} />
                </Field>
                <Field label="Nombre para mostrar" required tooltip="Título comercial visible" error={errors.displayName}>
                  <input className={`border rounded-md px-3 py-2 w-full ${errorClass('displayName')}`} name="displayName" value={form.displayName} onChange={onChange} />
                </Field>
                <Field label="Moneda" required tooltip="Moneda de precio" error={errors.currency}>
                  <select className={`border rounded-md px-3 py-2 w-full ${errorClass('currency')}`} name="currency" value={form.currency} onChange={onChange}>
                    <option value="USD">USD</option>
                    <option value="UYU">UYU</option>
                  </select>
                </Field>
                <Field label="Precio" required tooltip="Monto en la moneda seleccionada" error={errors.price}>
                  <input className={`border rounded-md px-3 py-2 w-full ${errorClass('price')}`} type="number" name="price" value={form.price} onChange={onChange} />
                </Field>
                <Field label="Tipo de pago" required tooltip="Periodicidad" error={errors.payType}>
                  <select className={`border rounded-md px-3 py-2 w-full ${errorClass('payType')}`} name="payType" value={form.payType} onChange={onChange}>
                    <option value="ANUAL">Anual</option>
                    <option value="MENSUAL">Mensual</option>
                    <option value="UNICO">Único</option>
                    <option value="REFERIDO">Referido</option>
                  </select>
                </Field>
                <Field label="Etiqueta de pago" tooltip="Texto corto como 'por mes'">
                  <input className="border rounded-md px-3 py-2 w-full" name="payTypeDisplay" value={form.payTypeDisplay || ''} onChange={onChange} />
                </Field>

              </div>
            </div>

            {/* Feature Toggles Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
              <Field label="Recursos" required tooltip="Compartidos o dedicados" error={errors.resources}>
                <div className="pt-2">
                  <IconToggle
                    checked={form.resources === 'DEDICADOS'}
                    onChange={(checked) => setForm((p: any) => ({ ...p, resources: checked ? 'DEDICADOS' : 'COMPARTIDOS' }))}
                    iconLeft={<Users size={16} />}
                    iconRight={<Server size={16} />}
                    labelLeft="Compartidos"
                    labelRight="Dedicados"
                  />
                </div>
              </Field>
              <Field label="Elegir centro de datos" tooltip="Permite al cliente elegir región">
                <div className="pt-2">
                  <Toggle checked={!!form.dataCenterSelectable} onChange={(val) => setForm((p: any) => ({ ...p, dataCenterSelectable: val }))} label="Habilitar elección" />
                </div>
              </Field>
              <Field label="SSL" tooltip="Certificado SSL gratuito">
                <div className="pt-2">
                  <Toggle checked={!!form.ssl} onChange={(val) => setForm((p: any) => ({ ...p, ssl: val }))} label="Certificado SSL gratis" />
                </div>
              </Field>
              <Field label="Cache" required tooltip="Tipo de caché" error={errors.cache}>
                <select className={`border rounded-md px-3 py-2 w-full ${errorClass('cache')}`} name="cache" value={form.cache} onChange={onChange}>
                  <option value="NONE">No</option>
                  <option value="SI">Sí</option>
                  <option value="DINAMICA">Dinámica</option>
                  <option value="OBJETOS">De objetos</option>
                  <option value="DINAMICA_OBJETOS">Dinámica y de objetos</option>
                </select>
              </Field>
            </div>

            {/* Additional Fields Below Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <Field label="Cantidad de sitios" required tooltip="Número de sitios permitidos" error={errors.sites}>
                <select className={`border rounded-md px-3 py-2 w-full ${errorClass('sites')}`} name="sites" value={form.sites} onChange={onChange}>
                  <option value="ONE">1</option>
                  <option value="TWO">2</option>
                  <option value="MULTIPLES">Múltiples</option>
                </select>
              </Field>
              <Field label="Sistema de hosting" required tooltip="Servidor web" error={errors.hostingSystem}>
                <select className={`border rounded-md px-3 py-2 w-full ${errorClass('hostingSystem')}`} name="hostingSystem" value={form.hostingSystem} onChange={onChange}>
                  <option value="APACHE">Apache</option>
                  <option value="NGINX">Nginx</option>
                  <option value="OTRO">Otro</option>
                </select>
              </Field>

              <Field label="Espacio GB" required tooltip="Almacenamiento incluido" error={errors.spaceGb}>
                <input className={`border rounded-md px-3 py-2 w-full ${errorClass('spaceGb')}`} type="number" name="spaceGb" value={form.spaceGb} onChange={onChange} />
              </Field>
              <Field label="E-mails" tooltip="Cuentas de correo incluídas">
                <input className="border rounded-md px-3 py-2 w-full" type="number" name="emails" value={form.emails} onChange={onChange} />
              </Field>
              <Field label="Bandwidth" tooltip="Transferencia mensual">
                <input className="border rounded-md px-3 py-2 w-full" name="bandwidth" value={form.bandwidth || ''} onChange={onChange} />
              </Field>
              <Field label="Seguridad" required tooltip="Nivel de seguridad" error={errors.security}>
                <SecuritySlider
                  value={form.security}
                  onChange={(val) => {
                    setForm((p: any) => ({ ...p, security: val }))
                    setDirty(true)
                  }}
                />
              </Field>
              <Field label="Respaldos" tooltip="Activar copias de seguridad">
                <div className="pt-2">
                  <Toggle checked={!!form.backups} onChange={(val) => setForm((p: any) => ({ ...p, backups: val }))} label="Activar" />
                </div>
              </Field>
              {form.backups && (
                <Field label="Tipo de respaldos" tooltip="Incrementales o completos">
                  <input className="border rounded-md px-3 py-2 w-full" name="backupsType" value={form.backupsType || ''} onChange={onChange} />
                </Field>
              )}


              <Field label="Optimización de imágenes" required tooltip="Herramienta o nivel de optimización" error={errors.imageOptimization}>
                <select className={`border rounded-md px-3 py-2 w-full ${errorClass('imageOptimization')}`} name="imageOptimization" value={form.imageOptimization} onChange={onChange}>
                  <option value="NONE">No</option>
                  <option value="SI">Sí</option>
                  <option value="SMUSH_PRO">Smush Pro</option>
                </select>
              </Field>
              <div className="col-span-1 md:col-span-2 lg:col-span-4">
                <Field label="Botón Paypal HTML" tooltip="Pega el HTML del botón">
                  <textarea className="border rounded-md px-3 py-2 w-full font-mono text-sm" rows={4} name="paypalButtonHtml" value={form.paypalButtonHtml || ''} onChange={onChange} placeholder="<form>...</form>" />
                  <PaypalReturnUrl planName={form.name} />
                </Field>
              </div>
            </div>
          </div>
        </div>

        {(form.resources === 'DEDICADOS' || form.dataCenterSelectable || Number(form.emails) > 0 || form.backups || form.security !== 'NONE') && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Detalles</h3>
            </div>
            <div className="p-5 space-y-4">
              {form.resources === 'DEDICADOS' && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700">Detalle de Recursos</div>
                  <RichEditor
                    value={form.resourcesDetail || ''}
                    onChange={(val) => setForm((p: any) => ({ ...p, resourcesDetail: val }))}
                    placeholder="Describe los recursos"
                  />
                </div>
              )}
              {form.dataCenterSelectable && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700">Detalle Centro de datos</div>
                  <RichEditor
                    value={form.dataCenterDetail || ''}
                    onChange={(val) => setForm((p: any) => ({ ...p, dataCenterDetail: val }))}
                    placeholder="Opciones de data center"
                  />
                </div>
              )}
              {Number(form.emails) > 0 && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700">Detalle de Mails</div>
                  <RichEditor
                    value={form.mailsDetail || ''}
                    onChange={(val) => setForm((p: any) => ({ ...p, mailsDetail: val }))}
                    placeholder="Capacidades de correo"
                  />
                </div>
              )}
              {form.backups && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700">Detalle de Respaldos</div>
                  <RichEditor
                    value={form.backupsDetail || ''}
                    onChange={(val) => setForm((p: any) => ({ ...p, backupsDetail: val }))}
                    placeholder="Frecuencia y tipo de backups"
                  />
                </div>
              )}
              {form.security !== 'NONE' && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-700">Detalle de Seguridad</div>
                  <RichEditor
                    value={form.securityDetail || ''}
                    onChange={(val) => setForm((p: any) => ({ ...p, securityDetail: val }))}
                    placeholder="Controles de seguridad"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold">Fotos</h3>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setShowMediaManager(true)} className="px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm font-medium">Gestor de Archivos</button>
              <button type="button" onClick={onPickPhotos} className="px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium">Subir fotos</button>
              <input ref={photosInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onUploadPhotos} />
            </div>
          </div>
          <div className="p-5">
            {Array.isArray(form.photos) && form.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {form.photos.map((url: string, idx: number) => (
                  <div key={idx} className="relative">
                    <img src={url} alt="foto" className="w-full h-24 object-cover rounded-md border" />
                    <button type="button" onClick={() => removePhoto(idx)} className="absolute top-1 right-1 bg-white/90 text-xs px-2 py-1 rounded-md border">Quitar</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border rounded-md h-40 flex items-center justify-center text-gray-500">Sin fotos agregadas</div>
            )}
          </div>
        </div>
      </form>
      </div>
      {showMediaManager && <MediaManager onClose={() => setShowMediaManager(false)} onSelect={(url) => {
        setForm((prev: any) => ({ ...prev, photos: [...(prev.photos || []), url] }))
        setDirty(true)
        setShowMediaManager(false)
      }} />}
    </div>
  )
}

export default AdminEditHosting
